/**
 * Uploading tree blobs to a Blossom server (BUD-01 / BUD-02).
 *
 * Nothing here is hashtree-specific: every node of the tree, manifests included,
 * is just an opaque blob as far as the server is concerned. That is the whole
 * point of the design — servers need no BUD-15/16/17/18 support at all.
 *
 * The one rule we have to be careful about: `k` values are bearer secrets and
 * MUST NOT be sent to a server. We only ever send ciphertext and its hash.
 */

import { finalizeEvent, type EventTemplate, type VerifiedEvent } from "nostr-tools/pure";
import { toHex } from "./bytes";

/**
 * Public servers that accept an anonymous upload of a CHK-encrypted blob.
 *
 * This list is shorter than the list of live Blossom servers, and the reason is
 * worth knowing: a hashtree uploads ciphertext and MessagePack manifests, so a
 * server that only accepts recognised media types cannot host one at all.
 * `blossom.band` rejects these with 415, and `blossom.nostr.hu` only serves a
 * pubkey whitelist — both are perfectly good Blossom servers that simply are
 * not usable for this.
 */
export const KNOWN_SERVERS = [
  "https://blssm.us",
  "https://nostr.download",
  "https://cdn.hzrd149.com",
  "https://blossom.ditto.pub",
];

export const DEFAULT_SERVER = KNOWN_SERVERS[0]!;

/** BUD-01 authorization event kind. */
export const AUTH_KIND = 24242;

export interface BlobDescriptor {
  url?: string;
  sha256: string;
  size?: number;
  type?: string;
  uploaded?: number;
}

/**
 * Build the kind 24242 authorization event for an upload.
 *
 * `x` commits the token to one specific blob hash, so a server can check that
 * the bytes it received are the bytes the user authorized.
 */
export function buildUploadAuth(blobHash: string, options: { expiresInSeconds?: number; content?: string } = {}): EventTemplate {
  const { expiresInSeconds = 300, content = "Upload hashtree blob" } = options;
  const now = Math.floor(Date.now() / 1000);
  return {
    kind: AUTH_KIND,
    created_at: now,
    content,
    tags: [
      ["t", "upload"],
      ["x", blobHash],
      ["expiration", String(now + expiresInSeconds)],
    ],
  };
}

/** Base64 of the signed auth event, as the `Authorization: Nostr ...` header wants. */
export function encodeAuthHeader(event: VerifiedEvent): string {
  const json = JSON.stringify(event);
  const base64 = btoa(String.fromCharCode(...new TextEncoder().encode(json)));
  return `Nostr ${base64}`;
}

export interface UploadResult {
  blobHash: string;
  descriptor: BlobDescriptor | null;
  /** The signed auth event, so the UI can show what was actually sent. */
  auth: VerifiedEvent;
  status: number;
  /** The server echoed back the same hash we computed. */
  hashMatches: boolean;
}

function normalizeServer(server: string): string {
  const trimmed = server.trim().replace(/\/+$/, "");
  return /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * `PUT /upload` one blob.
 *
 * `ciphertext` is what goes on the wire — the CHK key never leaves the browser.
 */
export async function uploadBlob(
  server: string,
  ciphertext: Uint8Array,
  blobHash: Uint8Array,
  secretKey: Uint8Array,
  contentType = "application/octet-stream",
): Promise<UploadResult> {
  const hash = toHex(blobHash);
  const auth = finalizeEvent(buildUploadAuth(hash), secretKey);

  const response = await fetch(`${normalizeServer(server)}/upload`, {
    method: "PUT",
    headers: {
      Authorization: encodeAuthHeader(auth),
      "Content-Type": contentType,
    },
    body: ciphertext.slice(),
  });

  if (!response.ok) {
    // BUD-01: servers explain themselves in X-Reason.
    const reason = response.headers.get("X-Reason") ?? (await response.text().catch(() => "")) ?? "";
    throw new Error(`${response.status} ${response.statusText}${reason ? ` — ${reason}` : ""}`);
  }

  const descriptor = (await response.json().catch(() => null)) as BlobDescriptor | null;
  return {
    blobHash: hash,
    descriptor,
    auth,
    status: response.status,
    hashMatches: descriptor?.sha256 === hash,
  };
}

/** `GET /<sha256>` — used by the final verification pass to prove the tree resolves. */
export async function fetchBlob(server: string, blobHash: string): Promise<Uint8Array> {
  const response = await fetch(`${normalizeServer(server)}/${blobHash}`);
  if (!response.ok) throw new Error(`could not fetch ${blobHash}: ${response.status} ${response.statusText}`);
  return new Uint8Array(await response.arrayBuffer());
}

/** Public URL a blob should be reachable at once uploaded. */
export function blobUrl(server: string, blobHash: string): string {
  return `${normalizeServer(server)}/${blobHash}`;
}
